import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { getProducts } from "@/redux/products/asyncActions";
import { cartSelector } from "@/redux/cart/selectors";
import { setCartItems } from "@/redux/cart/slice";
import { getCartItemsFromStorage, setCartItemsToStorage } from "@/utils";
import { ProductsPropsType } from "./types";
import { ProductCard, ProductSkeleton } from "./components";
import { IProduct as IProductBase } from "@/redux/products/types";
import { IProduct } from "@/redux/cart/types";
import { Button } from "@/components/Button";
import styles from "./Products.module.scss";

export const Products: React.FC<ProductsPropsType> = ({ title }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { products, isLoading, error } = useAppSelector((state) => state.products);
  const { totalCartItems } = useAppSelector(cartSelector);

  useEffect(() => {
    dispatch(getProducts());
  }, [dispatch]);

  useEffect(() => {
    const storageCartItems = getCartItemsFromStorage();
    if (storageCartItems) {
      dispatch(setCartItems(storageCartItems));
    }
  }, [dispatch]);

  const getProductQuantity = (productId: number): number => {
    const productsInCart = (totalCartItems.products || {}) as { [key: string]: IProduct };
    const product = productsInCart[productId.toString()];
    return product?.count || 0;
  };

  const hasProductsInCart = useMemo(() => {
    const productsInCart = (totalCartItems.products || {}) as { [key: string]: IProduct };
    return Object.keys(productsInCart).length > 0;
  }, [totalCartItems.products]);

  const handleGoToCart = () => {
    navigate("/cart");
  };

  const handleAddToCart = (product: IProductBase) => {
    const currentStorageCartItems = getCartItemsFromStorage();
    let productsStorage: { [key: string]: IProduct } = {};
    let seatsStorage = {};
    let racketsStorage = {};

    if (currentStorageCartItems) {
      productsStorage = (currentStorageCartItems.products || {}) as { [key: string]: IProduct };
      seatsStorage = currentStorageCartItems.seats || {};
      racketsStorage = currentStorageCartItems.rackets || {};
    }

    const productId = product.productId.toString();
    const existingProduct = productsStorage[productId];

    const productToAdd: IProduct = {
      ...product,
      count: existingProduct ? (existingProduct.count || 0) + 1 : 1,
    };

    const cartItemsForStorage = {
      products: { ...productsStorage, [productId]: productToAdd },
      seats: { ...seatsStorage },
      rackets: { ...racketsStorage },
    };

    setCartItemsToStorage(cartItemsForStorage);
    dispatch(setCartItems(cartItemsForStorage));
  };

  const handleRemoveFromCart = (productId: number) => {
    const currentStorageCartItems = getCartItemsFromStorage();
    let productsStorage: { [key: string]: IProduct } = {};
    let seatsStorage = {};
    let racketsStorage = {};

    if (currentStorageCartItems) {
      productsStorage = (currentStorageCartItems.products || {}) as { [key: string]: IProduct };
      seatsStorage = currentStorageCartItems.seats || {};
      racketsStorage = currentStorageCartItems.rackets || {};
    }

    const productIdStr = productId.toString();
    const existingProduct = productsStorage[productIdStr];

    if (existingProduct && existingProduct.count && existingProduct.count > 1) {
      // Уменьшаем количество
      const productToUpdate: IProduct = {
        ...existingProduct,
        count: existingProduct.count - 1,
      };

      const cartItemsForStorage = {
        products: { ...productsStorage, [productIdStr]: productToUpdate },
        seats: { ...seatsStorage },
        rackets: { ...racketsStorage },
      };

      setCartItemsToStorage(cartItemsForStorage);
      dispatch(setCartItems(cartItemsForStorage));
    } else if (existingProduct && existingProduct.count === 1) {
      // Удаляем товар из корзины
      const { [productIdStr]: removed, ...restProducts } = productsStorage;

      const cartItemsForStorage = {
        products: restProducts,
        seats: { ...seatsStorage },
        rackets: { ...racketsStorage },
      };

      setCartItemsToStorage(cartItemsForStorage);
      dispatch(setCartItems(cartItemsForStorage));
    }
  };

  if (isLoading) {
    return (
      <div className={styles.products}>
        <div className={styles.grid}>
          {[...Array(4)].map((_, index) => (
            <ProductSkeleton key={index} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        Произошла ошибка при загрузке товаров. Попробуйте позже.
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className={styles.empty}>
        Товары временно отсутствуют
      </div>
    );
  }

  return (
    <div className={styles.products}>
      <div className={styles.grid}>
        {products.map((product) => (
          <ProductCard
            key={product.productId}
            product={product}
            onAddToCart={handleAddToCart}
            onRemoveFromCart={handleRemoveFromCart}
            quantity={getProductQuantity(product.productId)}
          />
        ))}
      </div>
      {hasProductsInCart && (
        <div className={styles.cartButtonWrapper}>
          <Button onClickFunc={handleGoToCart} newStyles={styles.cartButton}>
            Оформить заказ
          </Button>
        </div>
      )}
    </div>
  );
};

